import { IOrderProduct } from 'sections/Order/types'
import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import {
  IDiscount,
  IIndivPrice,
  IOfferPrice,
  IPriceList,
} from 'featureSliced/shared/store/reducers/SpecialsReducer/types'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { ICartProduct } from 'featureSliced/shared/store/generalTypes'
import {
  IBucketProduct,
  ProductTemplateIdType,
} from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

import { getPriceWithoutDiscount } from './getPriceWithoutDiscount'
import { getPrice } from './getPrice'
import { useMySelector } from '../useMySelector'
import { getDiscountedPrice } from 'featureSliced/shared/utils/getDiscountedPrice'
import {
  getPrecision,
  roundValueTo,
} from 'featureSliced/shared/utils/roundValueTo'

export const getAllProductsPrice = (
  productList: IBucketProduct[],
  discounts: IDiscount[],
  individualPrices: IIndivPrice[],
  productListWithPrices: IPriceList,
  currency: string,
  offerPrices?: IOfferPrice[]
) => {
  const totalProductsPrice = productList?.reduce((acc, product) => {
    const productPrices = getProductPrice(
      product,
      product?.productTemplateId,
      product?.menuItems?.length || 0,
      discounts,
      individualPrices,
      productListWithPrices,
      currency,
      offerPrices
    )
    const totalMenuItemsPrice =
      product?.menuItems?.reduce((acc, menuItem) => {
        const itemPrice = getMenuItemPrice(menuItem as any, currency)
        // a component carries its own count — the desktop basket
        // (cartLineTotal), the server (calcOrderTotal) and the SmartCheckout all
        // charge count x price, this was the only place that did not (QRM-665)
        const count = Number((menuItem as any)?.count) || 1
        return (acc += (itemPrice?.finalPrice || 0) * count)
      }, 0) || 0
    return (acc +=
      (productPrices?.finalPrice + totalMenuItemsPrice) * product?.count)
  }, 0)
  return totalProductsPrice
}

export const getProductPrice = (
  product: IProduct | IOrderProduct | ICartProduct,
  productTemplateId: ProductTemplateIdType,
  menuItemsLength: number,
  discounts: IDiscount[],
  individualPrices: IIndivPrice[],
  productListWithPrices: IPriceList,
  currency: string,
  offerPrices?: IOfferPrice[]
) => {
  const productId = (product as any)?.id ?? (product as any)?.productId

  const productPrices =
    productListWithPrices?.[`${productId}-${productTemplateId}`]
  const discount = discounts?.find((dis) => dis.productId === productId)
  const individualPrice = individualPrices?.find(
    (item) => item.productId === productId
  )
  const offerPrice = offerPrices?.find((item) => item.productId === productId)

  let priceOG, finalPrice, discountValue
  let isOffer = false
  let offerDescription = ''

  switch (true) {
    case Boolean(menuItemsLength !== 0):
      priceOG = getPrice(product)
      finalPrice = getPrice(product)
      discountValue = 0
      break
    // Angebotspreis (CashAssistPrices Type 3) — must come before the discount and
    // individual-price cases: in Smart_Checkout an active offer beats Type 2, and an
    // offer vs a Type 5 % discount never stack — the CHEAPER of the two is charged.
    case Boolean(productTemplateId === 'offerPrice') || Boolean(offerPrice): {
      const offerValue = Number(offerPrice?.price) || 0
      const regular = getPrice(productPrices) || getPrice(product)
      const discounted =
        discount?.discountPercentage && regular
          ? roundValueTo(
              getDiscountedPrice(regular, discount.discountPercentage),
              getPrecision(currency)
            )
          : 0
      if (!offerValue) {
        priceOG = regular
        finalPrice = regular
        discountValue = 0
      } else if (discounted && discounted < offerValue) {
        priceOG = regular
        finalPrice = discounted
        discountValue = discount?.discountPercentage || 0
      } else {
        priceOG = regular
        finalPrice = offerValue
        discountValue = 0
        isOffer = true
        offerDescription = offerPrice?.description || ''
      }
      break
    }
    case Boolean(productTemplateId === 'discount') || Boolean(discount):
      discountValue = discount?.discountPercentage || 0
      priceOG = getPriceWithoutDiscount(productPrices)
      finalPrice = getPrice(productPrices)
      break
    case Boolean(productTemplateId === 'individualPrice') ||
      Boolean(individualPrice):
      priceOG = getPriceWithoutDiscount(individualPrice)
      finalPrice = getPrice(individualPrice)
      discountValue = 0
      break
    case typeof productTemplateId === 'number' && !individualPrice: {
      const priceFromList = getPrice(productPrices)
      priceOG = priceFromList
        ? getPriceWithoutDiscount(productPrices)
        : getPrice(product)
      finalPrice = priceFromList || getPrice(product)
      discountValue = 0
      break
    }
    default:
      priceOG = getPrice(product)
      finalPrice = getPrice(product)
      discountValue = 0
  }

  return {
    priceOG,
    priceOGString: convertToPrice(priceOG),
    finalPrice: finalPrice || priceOG,
    finalPriceString: convertToPrice(finalPrice || priceOG),
    discountValue,
    discountString: discountValue ? `-${discount?.discountPercentage}%` : '',
    isOffer,
    offerDescription,
    currency,
  }
}

const getMenuItemPrice = (menuItem: any, currency: string) => {
  const finalPrice = getPrice(menuItem)
  return {
    priceOG: finalPrice,
    priceOGString: convertToPrice(finalPrice),
    finalPrice,
    finalPriceString: convertToPrice(finalPrice),
    currency,
  }
}

export const useGetProductPrice = () => {
  const currency = useMySelector((state) => state.app.companyData.Currency)
  const { discounts, individualPrices, productListWithPrices, offerPrices } =
    useMySelector((state) => state.specials)
  const categories = useMySelector((state) => state.categories.items)

  // Replaces the persisted cart snapshot price with the live Angebotspreis right before
  // an order/payment payload is built. The snapshot can predate the offer window (cart
  // built yesterday, paid today) — the charge must match what the screen shows now.
  // Non-offer items pass through untouched; offerApplied tells the payload builders to
  // keep discountP at 0 (Smart_Checkout never stacks a % discount on an offer price).
  const applyOfferPricesToBucket = (
    products: IBucketProduct[]
  ): IBucketProduct[] =>
    products?.map((el) => {
      if (el.menuItems?.length) return el
      if (!offerPrices?.some((offer) => offer.productId === el.productId)) {
        return el
      }
      const catalogProduct = categories
        ?.flatMap((cat) => cat.products)
        ?.find((item) => item?.id === el.productId)
      const live = getProductPrice(
        (catalogProduct ?? el) as IProduct | ICartProduct,
        el.productTemplateId,
        0,
        discounts,
        individualPrices,
        productListWithPrices,
        currency,
        offerPrices
      )
      if (!live.isOffer) return el
      return { ...el, price: live.finalPrice, offerApplied: true }
    })

  return {
    getAllProductsPrice: (productsList: IBucketProduct[]) =>
      getAllProductsPrice(
        productsList,
        discounts,
        individualPrices,
        productListWithPrices,
        currency,
        offerPrices
      ),
    getProductPrice: (
      product: IProduct | IOrderProduct | ICartProduct,
      productTemplateId: ProductTemplateIdType,
      menuItemsLength = 0
    ) =>
      getProductPrice(
        product,
        productTemplateId,
        menuItemsLength,
        discounts,
        individualPrices,
        productListWithPrices,
        currency,
        offerPrices
      ),
    getMenuItemPrice: (menuItem: any) => getMenuItemPrice(menuItem, currency),
    applyOfferPricesToBucket,
  }
}
